import { of, from, throwError } from 'rxjs'
import { catchError, mergeMap, switchMap, tap } from 'rxjs/operators'
import Fetch from '../fetch'
import ConfigHelper from 'framework/helpers/config'
import DataHelper from 'framework/helpers/data'
import VoltError from 'VoltError'
import Constants from 'api-constants'
import * as factories from '../factories'
import { serialize } from 'helpers/index.js'
import TokenHelper from 'framework/helpers/token'
import { getHardware, getNagraHardwareType } from '../helpers'

export default class AuthApi extends Fetch {
    constructor(config, otherApis = {}) {
        super(config, otherApis)
        this.settingsApi = otherApis.settingsApi
        this.recordingsApi = otherApis.recordingsApi
        this.tokenHelper = new TokenHelper(
            this._refreshAccessToken,
            DataHelper.STORE_KEY.SECONDARY_ACCESS_TOKEN,
            this.logger
        )
    }

    /**
     * Retrieves the profile by using authentication token in cache
     * Called systematically by the UI at boot for seeking after authentication skipping authentication screen
     * If the token is missing or invalid, then throw an error to force the UI for authentication
     * @returns {Observable<Profile>}
     */
    getProfile() {
        return this.authenticate().pipe(
            mergeMap(() => this.fetchProfile()),
            catchError((error) => {
                return throwError(
                    new VoltError(VoltError.codes.LOGIN_REQUIRED, {
                        inheritedError: error,
                    })
                )
            })
        )
    }

    /**
     * Fetch the profile
     * @returns {Observable<Profile>}
     */
    fetchProfile() {
        const accessToken = DataHelper.getInstance().getSecondaryAccessToken()
        if (!accessToken) {
            const error = 'Cannot get profile due to empty Access Token'
            this.logger.error('AuthApi', error)
            return throwError(
                new VoltError(VoltError.codes.USER_PROFILE_RETRIEVAL_FAILED, {
                    extraLog: error,
                })
            )
        }
        return this.fetch({
            url: `${this._getAuthUrl()}/adm/v1/user/accounts`,
            method: 'GET',
            headers: {
                'Content-Type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                'Client-token': accessToken,
            },
            log: 'NAGRA OAUTH CLIENT LOGIN',
            defaultError: VoltError.codes.USER_PROFILE_RETRIEVAL_FAILED,
        }).pipe(
            mergeMap(({ response = {} }) => of(factories.profileFactory(response, this.config))),
            mergeMap((profile) => {
                if (profile.dvrEnabled) {
                    this.logger.info(
                        'DVR Enabled for this profile, retrieving quota from Nagra calling the storage api'
                    )
                    return this.recordingsApi.getStorage().pipe(
                        mergeMap((quota) => {
                            this.logger.info('Receiving user quota: ', quota)
                            if (quota) {
                                profile = profile.update({
                                    dvrQuota: quota.dvrQuota,
                                    dvrQuotaRemaining: quota.dvrQuotaRemaining,
                                    dvrQuotaProtected: quota.dvrQuotaProtected,
                                    dvrQuotaUnit: quota.unit,
                                })
                            }
                            return of(profile)
                        }),
                        catchError((error) => {
                            this.logger.warn(
                                'Unable to get the storage while fetching the profile due to the error: ',
                                error
                            )
                            return of(profile)
                        })
                    )
                }
                return of(profile)
            }),
            switchMap((profile) =>
                DataHelper.getInstance()
                    .storeData([[DataHelper.STORE_KEY.ACCOUNT_ID, profile.userId]])
                    .pipe(
                        // Should get PC level only after setting userId by DataHelper
                        // Because getPcLevel() uses DataHelper for getting accountId
                        mergeMap(() => this.settingsApi.getPcLevel()),
                        mergeMap((pcLevel) => of(profile.update({ pcLevel })))
                    )
            )
        )
    }

    /**
     * Username/Password Login
     * @param {Object} data
     * @param {String} data.oauthToken
     * @param {String} data.accountId
     * @param {Boolean} [data.useCache=false] indicates that it tries to login from cache
     * @param {Object} [data.deviceData] optional device data (brand, model, serial number, casn, etc...)
     * @returns {Observable<String>} Constants.authenticationStatus values
     */
    login({
        oauthToken,
        deviceData = {},
        accountId,
        useCache = false,
        forceFirstLogin = false,
    } = {}) {
        const { tenantId } = ConfigHelper.getInstance().getConfig('nagra')
        if (!tenantId) {
            return throwError(
                new VoltError(VoltError.codes.MISSING_TENANT_ID, {
                    extraLog: 'Missing tenant ID',
                })
            )
        }

        return this._getOpenDeviceAccountClientId(accountId).pipe(
            mergeMap((accountClientId) => {
                // On the Nagra Backend the client ID should be cached as much as possible because the client ID is retrieved only after the first login
                // This also implies to create a new device providing the 'deviceInformation' body only the first time creating a device and getting a clientID we should cache
                // Documentation: https://docs.nagra.com/doc/otv/otvdoc2051/integration-guides/client-integration-guide/authentication/use-cases#Usecases-Firsttimeandn-thtimesignon
                // As the client ID should be cached only local storage memory, it means every time the user install the application or do a clear data a new device is created
                // But we should be robust to the logout method avoiding to create a new device after a logout
                if (forceFirstLogin) {
                    accountClientId = undefined
                }
                const isNewNagraDevice = !accountClientId || forceFirstLogin
                let body
                if (isNewNagraDevice) {
                    const { HardwareOS = 'Android', HardwareType = 'STB' } = getNagraHardwareType(
                        this.config.platform
                    )

                    const { model, manufacturer } = getHardware(deviceData, this.config.platform)
                    const { width = 1920, height = 1080 } = deviceData?.screenDimensions || {}
                    const ScreenWidth = isNaN(width) ? 1920 : Math.floor(width)
                    const ScreenHeight = isNaN(height) ? 1080 : Math.floor(height)

                    body = {
                        // It seems when we provide this field an error occurs on the backend. Not a big deal as by default the backend creates "OPEN" devices
                        // deviceType: 'OPEN', // For OTT for now all projects with NAGRA are OTT (to handle 'MANAGED' later dynamically)
                        device: {
                            OS: {
                                type: HardwareOS,
                                version: deviceData.systemVersion || 'unknown',
                            },
                            screen: {
                                width: ScreenWidth,
                                height: ScreenHeight,
                                density: '0',
                            },
                            // There is a bug on Nagra, model, manufacturer and type should follow this specific order in the payload
                            hardware: {
                                model,
                                manufacturer,
                                type: HardwareType,
                            },
                        },
                    }
                }

                this.logger.info(
                    `[NAGRA] ${
                        isNewNagraDevice ? 'First' : 'Subsequent'
                    } Login (forceFirstLogin: ${forceFirstLogin})`
                )
                return this.fetch({
                    url: `${this._getAuthUrl()}/ias/v2/token/starhub${serialize({
                        client_id: accountClientId,
                        account_id: accountId,
                        access_token: oauthToken,
                    })}`,
                    method: 'POST',
                    headers: {
                        'Content-Type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                        tenantId: tenantId,
                    },
                    body,
                    log: 'NAGRA OAUTH CLIENT LOGIN',
                }).pipe(
                    tap(({ response = {} }) => {
                        this._saveOpenDeviceAccountClientId(accountId, response.client_id)
                    }),
                    mergeMap(({ response = {} }) => this._parseAccessToken(response)),
                    switchMap(() => of(Constants.authenticationStatus.SUCCEEDED)),
                    catchError((error) => {
                        if (
                            !isNewNagraDevice &&
                            !useCache &&
                            !forceFirstLogin &&
                            (error.code === VoltError.codes.AUTH_UNKNOWN_DEVICE.code ||
                                // As when device are deleted from nagra api, rely on inactive device error to try a first loggin
                                error.code === VoltError.codes.AUTH_INACTIVE_DEVICE.code)
                        ) {
                            this.logger.warn(
                                'Subsequent login with cached client id but backend returns Unknown device meaning it has been removed by another device, retry a first Login with device creation',
                                error
                            )
                            return this.login({
                                oauthToken,
                                deviceData,
                                accountId,
                                forceFirstLogin: true,
                            })
                        }

                        return throwError(error)
                    })
                )
            })
        )
    }

    /**
     * Register CASN (SecureID to keep unique parameter) to be used as cpeId (Customer Pre provisionned equipement)
     * This ID will be used to authenticate next requests
     * @param {Object} data
     * @param {String} data.secureId (alias casn)
     * @param {String} data.oauthToken
     * @param {Object} [data.deviceData] optional device data (brand, model, serial number, casn, etc...)
     * @returns {Observable}
     */
    loginWithSecureId({ secureId: casn, oauthToken, deviceData } = {}) {
        const { tenantId, useOldCasnApi = false } = ConfigHelper.getInstance().getConfig('nagra')
        if (!tenantId) {
            return throwError(
                new VoltError(VoltError.codes.MISSING_TENANT_ID, {
                    extraLog: 'Missing tenant ID',
                })
            )
        }

        // There are two APIs for CASN Login for Nagra
        // A - POST /ias/v1/starhub/token/actions/managed-signon-by-casn?grant_type=client_credentials&id-type=casn&casn=2208501794&access_token=ACCESS_TOKEN
        // B - POST /ias/v2/token?grant_type=client_credentials&id-type=casn&client_id=2208501794&access_token=ACCESS_TOKEN (client_id => CASN)
        // Legacy application is using Method B but NAGRA has strongly recommended to use Method A
        let url
        if (useOldCasnApi) {
            url = `${this._getAuthUrl()}/ias/v2/token${serialize({
                grant_type: 'client_credentials',
                'id-type': 'casn',
                client_id: casn,
                access_token: oauthToken,
            })}`
        } else {
            url = `${this._getAuthUrl()}/ias/v1/starhub/token/actions/managed-signon-by-casn${serialize(
                {
                    grant_type: 'client_credentials',
                    'id-type': 'casn',
                    casn: casn,
                    access_token: oauthToken,
                }
            )}`
        }

        return this.fetch({
            url,
            method: 'POST',
            headers: {
                'Content-Type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                tenantId: tenantId,
            },
            log: 'NAGRA CASN CLIENT LOGIN',
        }).pipe(
            mergeMap(({ response = {} }) => {
                return this._parseAccessToken(response)
            })
        )
    }

    /**
     * Logout user
     * @returns {Observable<>} true = token
     */
    logout({ skipRevocation = false } = {}) {
        if (skipRevocation) {
            this.tokenHelper.stopTokenRefreshTimer()
            return DataHelper.getInstance().clearData(this._getStoredKeys())
        }
        const { tenantId } = ConfigHelper.getInstance().getConfig('nagra')
        return this.fetch({
            url: `${this._getAuthUrl()}/ias/v2/signout`,
            method: 'POST',
            headers: {
                'Content-Type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                tenantId: tenantId,
                'Client-token': DataHelper.getInstance().getSecondaryRefreshToken(),
            },
            log: 'NAGRA SIGN OUT',
        }).pipe(
            mergeMap(() => {
                this.tokenHelper.stopTokenRefreshTimer()
                return DataHelper.getInstance().clearData(this._getStoredKeys())
            }),
            catchError((error) => {
                this.logger.warn(
                    'Error during NAGRA logout... wipe cache and return success',
                    error
                )
                this.tokenHelper.stopTokenRefreshTimer()
                return DataHelper.getInstance().clearData(this._getStoredKeys())
            })
        )
    }

    /**
     * This function follows the nomenclature from VOLT API.
     * This function does not do the authentication but only check if storage contains
     * Token cached, then refresh the access token if the expirations occurred
     * Throw en error if there is not authentication token
     * @param {Boolean} [args.needToRefreshToken] Need to refresh the access Token
     * @returns {Observable<Boolean>}
     */
    authenticate() {
        return DataHelper.getInstance()
            .readData(this._getStoredKeys(false))
            .pipe(
                mergeMap(() => {
                    // If we do not have an access token in storage, redo the authentication process
                    if (!this.tokenHelper.hasToken()) {
                        return throwError(
                            new VoltError(VoltError.codes.INVALID_AUTH_TOKEN, {
                                extraLog: 'No Access token found from cache',
                            })
                        )
                    }

                    let needToRefreshToken = false
                    // If the access token has expired, just refresh it without redoing the login process
                    if (!this.tokenHelper.isTokenValid()) {
                        needToRefreshToken = true
                        this.logger.info(
                            'Init : Access Token is no more valid, try to refresh the access token'
                        )
                    }

                    if (needToRefreshToken) {
                        return this._refreshAccessToken().pipe(
                            mergeMap(() => {
                                return of(true)
                            }),
                            catchError((error) => {
                                if (VoltError.codes.INVALID_AUTH_TOKEN.code === error.code) {
                                    return throwError(
                                        new VoltError(VoltError.codes.INVALID_AUTH_TOKEN, {
                                            extraLog:
                                                'Invalid Token got from the backend. Force to re-login',
                                            inheritedError: error,
                                        })
                                    )
                                } else {
                                    // Do not invite the user to login again for irrelevant issue. Could create more issue than the optimization desired
                                    this.logger.error(
                                        `Init : An error occurred during the refresh token ${error}. But do not invite the user to login again`
                                    )
                                    return of(true)
                                }
                            })
                        )
                    }

                    this.tokenHelper.startTokenRefreshTimer()

                    return of(true)
                })
            )
    }

    /**
     * Refresh the Access Token
     * @returns {Observable<>}
     */
    _refreshAccessToken = () => {
        const { tenantId } = ConfigHelper.getInstance().getConfig('nagra')
        if (!tenantId) {
            return throwError(
                new VoltError(VoltError.codes.MISSING_TENANT_ID, {
                    extraLog: 'Missing tenant ID',
                })
            )
        }

        return this.fetch({
            url: `${this._getAuthUrl()}/ias/v2/token${serialize({
                grant_type: 'refresh_token',
                refresh_token: DataHelper.getInstance().getSecondaryRefreshToken(),
            })}`,
            method: 'POST',
            headers: {
                'Content-Type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                tenantId: tenantId,
            },
            log: 'NAGRA REFRESH TOKEN',
        }).pipe(
            mergeMap(({ response = {} }) => {
                return this._parseAccessToken(response)
            })
        )
    }

    /**
     * Out of home feature not supported
     * @returns {Observable<boolean>} False
     */
    getIsOutOfHome() {
        return of(false)
    }

    /**
     * Get the "suspended" state of current user
     * @returns {Observable<boolean>} true = is suspended otherwise false
     */
    getIsSuspended() {
        return of(false)
    }

    /**
     * Init Guest Mode
     * @returns {Observable<boolean>} True
     */
    initGuestMode() {
        return of(true)
    }

    // -----------------------------------------------------------
    // ----------                TOOLBOX                ----------
    // -----------------------------------------------------------
    /**
     * @returns {String} The Auth URL
     */
    _getAuthUrl() {
        // As this implementation of "nagra" is not as a secondary backend for the authentication,
        // use apiUrl to avoid to add complexity with another URL but for future project with "Nagra" should be reworked
        return this.config.urls.apiUrl
    }

    /**
     * Return list of keys stored in local memory
     * @param {Object} forDeletion call for deletion from storage
     * @returns {Array<String>}
     */
    _getStoredKeys(forDeletion = true) {
        return [
            DataHelper.STORE_KEY.SECONDARY_ACCESS_TOKEN,
            DataHelper.STORE_KEY.SECONDARY_REFRESH_TOKEN,
            DataHelper.STORE_KEY.SECONDARY_ACCESS_TOKEN_EXPIRATION_TIME_MS,
            DataHelper.STORE_KEY.SECONDARY_ACCESS_TOKEN_ISSUED_TIME_MS,
            !forDeletion && DataHelper.STORE_KEY.ACCOUNT_ID,
        ].filter(Boolean)
    }

    /**
     * Retrieve client id cached for the management of the first Login on Nagra (only for OPEN DEVICES)
     * @param {String} accountId
     * @returns {Observable<String>} Client ID per Account
     */
    _getOpenDeviceAccountClientId(accountId) {
        // Do not use DataHelper here as it could invalidates the mechanism of allowed stored keys
        return from(this.config.persistentStorage.getItem(`CLIENT_ID_ACCOUNT_${accountId}`)).pipe(
            mergeMap((cachedClientId) => {
                return of(!cachedClientId ? undefined : cachedClientId)
            }),
            catchError(() => {
                return of(undefined)
            })
        )
    }

    /**
     * Retrieve client id cached for the management of the first Login on Nagra (only for OPEN DEVICES)
     * @param {String} accountId
     * @returns {Observable}
     */
    _saveOpenDeviceAccountClientId(accountId, clientId) {
        // Do not use DataHelper here as it could invalidates the mechanism of allowed stored keys
        return from(
            this.config.persistentStorage.setItem(`CLIENT_ID_ACCOUNT_${accountId}`, clientId)
        )
    }

    /**
     * Parse the access token issued by Nagra
     * @param {Object} response
     * @param {String} response.access_token
     * @param {String} response.refresh_token
     * @param {String} response.accountId
     * @param {String} response.client_id
     * @param {Number} response.expires_in
     * @returns {Observable}
     */
    _parseAccessToken = (response) => {
        const { access_token, refresh_token, expires_in, accountId, client_id } = response || {}

        if (!access_token || !refresh_token || !expires_in) {
            const errorMsg =
                'Access Token, Refresh Token or expiration is missing from the backend, cannot validate Authentication !!!'

            const nextAttemptMissingParams = 50000 // In case of error retry in few secondes
            this.tokenHelper.startTokenRefreshTimer(nextAttemptMissingParams)

            this.logger.error(errorMsg)
            return throwError(
                new VoltError(VoltError.codes.INVALID_AUTH_TOKEN, {
                    extraLog: errorMsg,
                })
            )
        }

        // WARNING: Should not lost the client ID otherwise need to recreate a device (only for VSTB)
        const clientId =
            client_id || DataHelper.getInstance().getData(DataHelper.STORE_KEY.SECONDARY_CLIENT_ID)
        return DataHelper.getInstance()
            .clearData(this._getStoredKeys())
            .pipe(
                mergeMap(() => {
                    const nextExpirationMs = expires_in * 1000
                    return DataHelper.getInstance()
                        .storeData([
                            [DataHelper.STORE_KEY.SECONDARY_ACCESS_TOKEN, access_token],
                            [DataHelper.STORE_KEY.SECONDARY_REFRESH_TOKEN, refresh_token],
                            [
                                DataHelper.STORE_KEY.SECONDARY_ACCESS_TOKEN_EXPIRATION_TIME_MS,
                                nextExpirationMs + Date.now(),
                            ],
                            [
                                DataHelper.STORE_KEY.SECONDARY_ACCESS_TOKEN_ISSUED_TIME_MS,
                                Date.now(),
                            ],
                            [DataHelper.STORE_KEY.SECONDARY_CLIENT_ID, clientId],
                            [DataHelper.STORE_KEY.ACCOUNT_ID, accountId],
                        ])
                        .pipe(
                            tap(() => {
                                this.tokenHelper.startTokenRefreshTimer(nextExpirationMs)
                            })
                        )
                })
            )
    }

    // -----------------------------------------------------------
    // ---------- FEATURES NOT SUPPORTED BY THE BACKEND ----------
    // -----------------------------------------------------------
    loginWithSecondaryDevice({ deviceId }) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[DAG] Feature yet not implemented',
            })
        )
    }

    isLoginWithSecondaryDevice() {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[DAG] Feature yet not implemented',
            })
        )
    }

    signUp(authFactor, data) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[SIGN UP] Feature yet not implemented',
            })
        )
    }

    validateOTP(authType, data) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[AUTH 2FA USING OTP] Feature yet not implemented',
            })
        )
    }

    isBackendAvailable = () => {
        return of(true)
    }
}
